import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Item, registry, RenderProps } from '../Item';
import { DROPDOWN } from '~/training/sequence/templates/items';
import { Button } from '~/ui/buttons';
import { QuizButton } from '~/ui/buttons/QuizButton';
import { PickList } from '~/ui/pick-list/PickList';
import { Modal } from '~/ui/modal/Modal';
import { Overlay } from '~/ui/overlay/Overlay';
import { Flex } from '~/ui/flex/Flex';
import audioEffectRightAnswer from '~/assets/mp3/right_answer.mp3';
import audioEffectWrongAnswer from '~/assets/mp3/wrong_answer.mp3';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { useQuizDropdownStore } from '~/training/sequence/templates/items/quiz/QuizDropdownStore';
import { t } from 'ttag';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { select } from '~/lib/select';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { TrainingState, useTrainingStore } from '~/training/state';

export type DropdownDefinition = {
  label: string;
  correct: boolean;
  feedback: string;
  feedbackAudios?: Array<{ mediaUrl: string; subtitlesUrl?: string }>;
  incorrectFeedbackAudios?: Array<{ mediaUrl: string; subtitlesUrl?: string }>;
  used?: boolean;
  id?: number;
  questionId?: string;
};

interface Props {
  group: string;
  items?: Array<DropdownDefinition>;
}

const selector = (s: MediaPlayerStore) => select(s, 'loadMedia', 'reset');
const questionSelector = (s: TrainingState) =>
  select(s, 'saveQuestionAnswers', 'status');

export class DropDown extends Item<typeof DROPDOWN, Props> {
  protected render({ group, id }: RenderProps<Props>) {
    const { saveQuestionAnswers, status } = useTrainingStore(questionSelector);
    const { loadMedia, reset } = useMediaPlayerStore(selector);
    const { items, useItem, unUseItem } = useQuizDropdownStore();
    const { addBlocker, removeBlocker, sendRxdInteractions } =
      useSequenceStore();

    const [selected, setSelected] = useState('');
    const [sStatus, setStatus] = useState('');
    const [modal, setModal] = useState(false);
    const [pickListItems, setPickList] = useState<Array<string>>([]);

    const [playAudioEffectRightAnswer] = useAudioEffect(audioEffectRightAnswer);
    const [playAudioEffectWrongAnswer] = useAudioEffect(audioEffectWrongAnswer);
    const selectedOption = ((items as Array<DropdownDefinition>) || []).find(
      el => el.label == selected
    );

    if (!group) {
      group = id;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let RXD: any;

    try {
      if (window.document.referrer) {
        const preUrl = window?.document?.referrer;
        const domain = preUrl?.split('/')[2];

        if (domain !== 'localhost:8000' && domain !== 'www.easyllama.com') {
          RXD = (window.parent as any).RXD;
        }
      } else {
        RXD = (window.parent as any).RXD;
      }
    } catch (error) {
      console.error('RXD in BTN:', error);
    }

    //generate list items for PickList, because we need hide items if it was selected in another dropdown list
    useEffect(() => {
      const pickListItems: Array<string> = [];

      items.forEach((el: DropdownDefinition) => {
        if (!el?.used) {
          pickListItems.push(el?.label);
        }
      });

      setPickList(pickListItems);
    }, [items]);

    const [tmp, currentId, questionId] = id.split('_');

    useEffect(() => {
      let mediaUrl = '';
      let subtitlesUrl = '';

      if (selectedOption && selectedOption?.questionId) {
        if (status !== 'completed') {
          saveQuestionAnswers(selectedOption.questionId);
        }
      }

      if (selectedOption && selectedOption.feedbackAudios) {
        mediaUrl = selectedOption.feedbackAudios[0]?.mediaUrl as string;
        subtitlesUrl = selectedOption.feedbackAudios[0]?.subtitlesUrl as string;

        //if user select correct option but not in correct place we need play another audio feedback
        if (
          selectedOption?.correct &&
          sStatus == 'wrong' &&
          selectedOption?.incorrectFeedbackAudios
        ) {
          mediaUrl = selectedOption?.incorrectFeedbackAudios[0]
            ?.mediaUrl as string;
          subtitlesUrl = selectedOption?.incorrectFeedbackAudios[0]
            ?.subtitlesUrl as string;
        }
      }

      if (
        selectedOption &&
        !selectedOption.feedbackAudios &&
        selectedOption?.correct &&
        sStatus == 'wrong' &&
        selectedOption?.incorrectFeedbackAudios
      ) {
        mediaUrl = selectedOption?.incorrectFeedbackAudios[0]
          ?.mediaUrl as string;
        subtitlesUrl = selectedOption?.incorrectFeedbackAudios[0]
          ?.subtitlesUrl as string;
      }

      if (mediaUrl) {
        reset();
        loadMedia(mediaUrl, subtitlesUrl, 'audio', true);
      }

      if (!selectedOption || !selectedOption.correct) {
        addBlocker(group);
        return () => removeBlocker(group);
      }
    }, [selectedOption]);

    //Play audio effect
    useEffect(() => {
      const sStatus =
        selectedOption?.correct && Number(currentId) == selectedOption?.id
          ? 'correct'
          : 'wrong';

      setStatus(sStatus);

      if (
        sendRxdInteractions &&
        RXD &&
        selectedOption &&
        selectedOption.id != null &&
        selectedOption.id >= 0
      ) {
        RXD.RecordTrueFalseInteraction(
          questionId,
          items[selectedOption?.id].label,
          sStatus == 'correct' ? true : false,
          true
        );
      }

      if (selected) {
        sStatus == 'correct'
          ? playAudioEffectRightAnswer()
          : playAudioEffectWrongAnswer();
      }
    }, [selected]);

    const selectItem = (newValue: string) => {
      unUseItem(selected);
      useItem(newValue);
      setSelected(newValue);
      setModal(false);
    };

    const FletListWithItems = React.forwardRef((props, ref) => {
      return (
        <Flex
          variant="list"
          ref={ref as React.RefObject<HTMLDivElement>}
          autoFocus
          aria-haspopup
          aria-label={t`Please Select`}
        >
          <h2>{t`Please Select`}</h2>
          <PickList
            selected={selected}
            items={pickListItems}
            onSelect={selectItem}
          />
        </Flex>
      );
    });

    const ref = useRef<HTMLDivElement>();

    const showModal = () => {
      setModal(true);
      setTimeout(() => {
        ref.current?.focus();
      }, 0);
    };

    const escFunction = useCallback(e => {
      if (e.keyCode === 27) {
        setModal(false);
      }
    }, []);

    useEffect(() => {
      document.addEventListener('keydown', escFunction);
      return () => document.removeEventListener('keydown', escFunction);
    }, [escFunction]);

    return (
      <>
        {!selected ? (
          <Button onClick={showModal}>{t`Please Select`}</Button>
        ) : (
          <QuizButton
            variant={
              selectedOption?.correct && Number(currentId) == selectedOption?.id
                ? 'correct'
                : 'wrong'
            }
            onClick={showModal}
          >
            {selected}
          </QuizButton>
        )}

        <Overlay open={modal} onClick={() => setModal(false)}>
          <Modal horizontal="center" open vertical="center">
            <FletListWithItems ref={ref as React.RefObject<HTMLDivElement>} />
          </Modal>
        </Overlay>
      </>
    );
  }
}

registry[DROPDOWN] = DropDown;
